import React, { useLayoutEffect, useRef } from 'react';
import { useIsMounted, useKeydown } from '@ardoq/hooks';
import {
  FullscreenDiv,
  ModalBackdrop,
  ModalContentContainer,
} from './modalAtoms';
import { MODAL_HEADER_TITLE_ID } from '../components/ModalHeader';

// This hook resets the transform property after the transition
// has ended. If this is not reset any child component of the modal
// using position fixed will see the modal as the containing block
// and not the viewport (see: https://developer.mozilla.org/en-US/docs/Web/CSS/position).
//
// Our current WithDropdown component relies on this behavior to position
// the dropdown menu correctly.
const useResetTransform = (ref: React.RefObject<HTMLElement | null>) => {
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;

    const removeTransform = () => {
      node.style.transform = 'none';
    };

    node.addEventListener('transitionend', removeTransform, false);
    return () => {
      node.removeEventListener('transitionend', removeTransform);
    };
  }, [ref]);
};

const isInputElement = (
  element: Element | null
): element is HTMLInputElement | HTMLTextAreaElement =>
  element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement;

const focusContainer = (element: HTMLElement, containerSelectors: string) => {
  const target = element.closest(containerSelectors);
  if (!(target instanceof HTMLElement)) {
    return;
  }
  target.setAttribute('tabindex', '0');
  target.addEventListener('blur', () => target.removeAttribute('tabindex'), {
    once: true,
  });
  target.focus();
  return;
};

/**
 *
 * @param onEscPress The function to call when the Escape key is pressed. Note that it will not be called if the Escape key is pressed while an input element is focused.
 * @param containerSelectors If the Escape key is pressed while an input is focused, the focus will be set to the closest element matching the selectors.
 */
export const handleEscapeButtonPressed =
  (onEscPress: (() => unknown) | undefined, containerSelectors: string) =>
  ({ key }: KeyboardEvent) => {
    if (key !== 'Escape' || !onEscPress) {
      return;
    }
    const { activeElement } = document;
    // If an input element is focused, blur it instead of closing the modal.
    // Directly calling blur on the input won't work because the wrapping
    // FocusScope component enforces the activeElement to be inside the
    // FocusScope. As a workaround, we focus the modal container instead. It
    // avoids interfering with the tab order by setting a tabindex only
    // temporarily.
    if (isInputElement(activeElement)) {
      focusContainer(activeElement, containerSelectors);
      return;
    }
    return onEscPress();
  };

export const ModalContainer = ({
  onBackdropClick,
  onEscPress = () => {},
  children,
}: React.PropsWithChildren<{
  onBackdropClick?: () => void;
  onEscPress?: () => unknown;
}>) => {
  const containerSelector = String(ModalContentContainer);
  useKeydown(handleEscapeButtonPressed(onEscPress, containerSelector));
  const isVisible = useIsMounted();
  const modalContainerRef = useRef<HTMLDivElement | null>(null);
  useResetTransform(modalContainerRef);

  return (
    <FullscreenDiv>
      <ModalBackdrop onClick={onBackdropClick} $isVisible={isVisible} />
      <ModalContentContainer
        role="dialog"
        aria-labelledby={MODAL_HEADER_TITLE_ID}
        $isVisible={isVisible}
        ref={modalContainerRef}
      >
        {children}
      </ModalContentContainer>
    </FullscreenDiv>
  );
};
